import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, RefreshCw, Gem, Layers, Boxes, Package, Hash, AlertTriangle,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n, d = 2) =>
  (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (n) => (n ?? 0).toLocaleString('en-IN');

const SummaryCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-gray-400 mb-1">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const StoneStockReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('branch'); // 'branch' | 'category'
  const [expanded, setExpanded] = useState({});

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/stone-stock-report');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stone stock from ERP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const toggle = (key, currentOpen) => setExpanded((p) => ({ ...p, [key]: !currentOpen }));

  const summary = data?.summary || {};

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="stone-stock-report-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700"
              onClick={() => navigate(-1)} data-testid="stone-stock-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Gem className="w-5 h-5" /> Stone Stock Report
              </h1>
              <p className="text-xs text-emerald-200">Live loose-stone inventory · admin & owner only</p>
            </div>
          </div>
          <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8"
            onClick={fetchReport} disabled={loading} data-testid="stone-stock-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400" data-testid="stone-stock-loading">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 mb-3" />
            <p className="text-sm">Fetching live stock from ERP…</p>
          </div>
        ) : !data ? (
          <div className="text-center py-24 text-gray-400">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Could not load the report.</p>
            <Button className="mt-4" onClick={fetchReport}>Try again</Button>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-5">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stone-stock-summary">
              <SummaryCard icon={Gem}    label="Total Carats"   value={fmt(summary.total_carats)}    testid="summary-carats" />
              <SummaryCard icon={Boxes}  label="Total Stone Wt" value={fmt(summary.total_stone_wt, 3)} sub="grams" testid="summary-stone-wt" />
              <SummaryCard icon={Hash}   label="Tags"           value={fmtInt(summary.total_tags)}    testid="summary-tags" />
              <SummaryCard icon={Layers} label="Categories"     value={fmtInt(summary.category_count)} testid="summary-categories" />
              <SummaryCard icon={Package} label="Stores w/ Stock" value={fmtInt(summary.branch_count)} sub={`of ${data.branches.length}`} testid="summary-branches" />
            </div>

            {/* ERP fetch errors */}
            {data.errors?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="stone-stock-errors">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Some stores could not be reached
                </div>
                <ul className="list-disc ml-6 text-xs">
                  {data.errors.map((e, i) => <li key={i}>{e.label}: {e.error}</li>)}
                </ul>
              </div>
            )}

            {/* View toggle */}
            <div className="flex items-center gap-2" data-testid="stone-stock-view-toggle">
              <button
                onClick={() => setView('branch')}
                data-testid="view-by-branch"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'branch' ? 'bg-[#075E54] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                By Store
              </button>
              <button
                onClick={() => setView('category')}
                data-testid="view-by-category"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'category' ? 'bg-[#075E54] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
                By Stone Category
              </button>
            </div>

            {/* BY STORE */}
            {view === 'branch' && (
              <div className="space-y-3" data-testid="stone-stock-by-branch">
                {data.branches.map((b) => {
                  const key = `b-${b.key}`;
                  const open = expanded[key] ?? (b.tag_count > 0);
                  return (
                    <div key={b.key} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`branch-card-${b.key}`}>
                      <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                        onClick={() => toggle(key, open)} data-testid={`branch-toggle-${b.key}`}>
                        <div className="flex items-center gap-2">
                          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="font-semibold text-gray-900">{b.label}</span>
                          {b.tag_count === 0 && <span className="text-xs text-gray-400 ml-1">(no stock)</span>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span><b className="text-gray-900">{fmt(b.total_carats)}</b> ct</span>
                          <span><b className="text-gray-900">{fmt(b.total_stone_wt, 3)}</b> g</span>
                          <span><b className="text-gray-900">{fmtInt(b.tag_count)}</b> tags</span>
                        </div>
                      </button>
                      {open && b.categories.length > 0 && (
                        <div className="overflow-x-auto border-t border-gray-100">
                          <table className="w-full text-sm" data-testid={`branch-table-${b.key}`}>
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                              <tr>
                                <th className="text-left px-4 py-2 font-semibold">Stone Category</th>
                                <th className="text-right px-4 py-2 font-semibold">Carats</th>
                                <th className="text-right px-4 py-2 font-semibold">Stone Wt (g)</th>
                                <th className="text-right px-4 py-2 font-semibold">Tags</th>
                              </tr>
                            </thead>
                            <tbody>
                              {b.categories.map((c) => (
                                <tr key={c.category} className="border-t border-gray-50 hover:bg-emerald-50/40">
                                  <td className="px-4 py-2 text-gray-800">{c.category}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmt(c.carats)}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmt(c.stone_wt, 3)}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmtInt(c.tag_count)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* BY CATEGORY */}
            {view === 'category' && (
              <div className="space-y-3" data-testid="stone-stock-by-category">
                {data.categories.length === 0 && (
                  <div className="text-center py-16 text-gray-400">No stone stock currently available.</div>
                )}
                {data.categories.map((c) => {
                  const key = `c-${c.category}`;
                  const open = expanded[key] ?? false;
                  return (
                    <div key={c.category} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`category-card-${c.category}`}>
                      <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                        onClick={() => toggle(key, open)} data-testid={`category-toggle-${c.category}`}>
                        <div className="flex items-center gap-2">
                          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="font-semibold text-gray-900">{c.category}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span><b className="text-gray-900">{fmt(c.total_carats)}</b> ct</span>
                          <span><b className="text-gray-900">{fmt(c.total_stone_wt, 3)}</b> g</span>
                          <span><b className="text-gray-900">{fmtInt(c.tag_count)}</b> tags</span>
                        </div>
                      </button>
                      {open && (
                        <div className="overflow-x-auto border-t border-gray-100">
                          <table className="w-full text-sm" data-testid={`category-table-${c.category}`}>
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                              <tr>
                                <th className="text-left px-4 py-2 font-semibold">Store</th>
                                <th className="text-right px-4 py-2 font-semibold">Carats</th>
                                <th className="text-right px-4 py-2 font-semibold">Stone Wt (g)</th>
                                <th className="text-right px-4 py-2 font-semibold">Tags</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.branches.map((br) => (
                                <tr key={br.key} className="border-t border-gray-50 hover:bg-emerald-50/40">
                                  <td className="px-4 py-2 text-gray-800">{br.label}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmt(br.carats)}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmt(br.stone_wt, 3)}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{fmtInt(br.tag_count)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-center text-xs text-gray-400 pt-2">
              Live from ERP · generated {data.generated_at ? new Date(data.generated_at).toLocaleString('en-IN') : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoneStockReport;
